import React, { useState, useCallback } from 'react';
import { useRegisterNudgeTarget } from '@atlassiansox/nudge-tooltip';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import { TeamsIcon } from '@atlaskit/temp-nav-app-icons/teams';
import { ButtonItem } from '@atlaskit/menu';
import { IconButton } from '@atlaskit/button/new';
import AddIcon from '@atlaskit/icon/core/add';
import { Stack, xcss } from '@atlaskit/primitives';
import { LinkMenuItem } from '@atlaskit/navigation-system';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import {
	LEVEL_TWO,
	LEVEL_ONE,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/level.tsx';
import { SECTION_ITEM_TEAMS } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/section-type.tsx';
import { MENU_ID_ANALYTIC_EVENT_IDS } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/side-nav.tsx';
import { MENU_ID_TEAMS } from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/common/constants.tsx';
import { getNavigateToTeamsApp } from '@atlassian/jira-teams-app-config/src/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import {
	FIXED_ITEM_DROPDOWN_ACTION,
	FIXED_ITEM_BUTTON,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/component-type.tsx';
import { useCreateTeamModal } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-teams/src/ui/use-create-team-modal/index.tsx';
import { useInvitePeopleModal } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-teams/src/ui/use-invite-people-modal.tsx';
import { InvitePeopleModalContextProvider } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-teams/src/controllers/use-invite-people-modal/index.tsx';
import { JWM_INVITE_TEAM_NUDGE_ID } from '@atlassian/jira-onboarding-core/src/constants.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { InviteTeamNudgeAsync } from '@atlassian/jira-software-onboarding-nudges--next/src/ui/jwm-invite-team-tour/async.tsx';

import { useIntl } from '@atlassian/jira-intl';
import type { AppProps } from '../types.tsx';
import messages from './messages.tsx';

const CREATE_A_TEAM = 'createATeam';
const INVITE_PEOPLE = 'invitePeople';

const PopupContent = ({
	openTeamsPopup,
	createTeamModalTrigger,
}: {
	openTeamsPopup: (isOpen: boolean) => void;
	createTeamModalTrigger: () => void;
}) => {
	const invitePeopleModalTrigger = useInvitePeopleModal();

	const actionSubjectId = MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_TEAMS];

	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onButtonClick = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({}), {
			action: 'clicked',
			actionSubject: 'dropdownItem',
			actionSubjectId,
			attributes: {
				level: LEVEL_TWO,
				componentType: FIXED_ITEM_DROPDOWN_ACTION,
				parentItemId: SECTION_ITEM_TEAMS,
				itemId: INVITE_PEOPLE,
			},
		});
		invitePeopleModalTrigger();
		openTeamsPopup(false);
	}, [createAnalyticsEvent, actionSubjectId, invitePeopleModalTrigger, openTeamsPopup]);
	const onCreateTeamClick = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({}), {
			action: 'clicked',
			actionSubject: 'dropdownItem',
			actionSubjectId,
			attributes: {
				level: LEVEL_TWO,
				componentType: FIXED_ITEM_DROPDOWN_ACTION,
				parentItemId: SECTION_ITEM_TEAMS,
				itemId: CREATE_A_TEAM,
			},
		});
		createTeamModalTrigger();
		openTeamsPopup(false);
	}, [createAnalyticsEvent, actionSubjectId, createTeamModalTrigger, openTeamsPopup]);
	return (
		<Stack xcss={teamsMenuStyle}>
			<ButtonItem onClick={onButtonClick}>{formatMessage(messages.invitePeople)}</ButtonItem>
			<ButtonItem onClick={onCreateTeamClick}>{formatMessage(messages.createTeam)}</ButtonItem>
		</Stack>
	);
};

function TeamsInner({ onClick }: AppProps) {
	const [isOpenTeamsPopup, openTeamsPopup] = useState(false);
	const { formatMessage } = useIntl();
	const label = formatMessage(messages.teams);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [createTeamModal, createTeamModalTrigger] = useCreateTeamModal();
	const actionSubjectId = MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_TEAMS];

	const peopleMenuWrapperRef = useRegisterNudgeTarget<HTMLAnchorElement>(
		JWM_INVITE_TEAM_NUDGE_ID,
		fg('teams-app-shortcut-nudge'),
	).ref;

	const { href } = getNavigateToTeamsApp({
		type: 'LANDING',
	});

	const onPlusButtonClick = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({}), {
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId,
			attributes: {
				level: LEVEL_ONE,
				componentType: FIXED_ITEM_BUTTON,
				parentItemId: SECTION_ITEM_TEAMS,
				itemId: 'add',
			},
		});
		openTeamsPopup(true);
	}, [createAnalyticsEvent, actionSubjectId]);

	const renderContent = useCallback(() => {
		return (
			<PopupContent
				openTeamsPopup={openTeamsPopup}
				createTeamModalTrigger={createTeamModalTrigger}
			/>
		);
	}, [createTeamModalTrigger, openTeamsPopup]);

	return (
		<>
			{createTeamModal}
			{fg('teams-app-shortcut-nudge') && <InviteTeamNudgeAsync />}

			<LinkMenuItem
				ref={fg('teams-app-shortcut-nudge') ? peopleMenuWrapperRef : undefined}
				href={href}
				elemAfter={<LinkExternalIcon label="" spacing="spacious" size="small" />}
				elemBefore={<TeamsIcon />}
				target="_blank"
				onClick={onClick}
				testId="navigation-apps-sidebar-nav4-sidebars-content-global-app-shortcuts.ui.teams.menu-link-item"
				actionsOnHover={
					<>
						<Popup
							shouldRenderToParent
							placement="bottom-start"
							messageId="navigation-apps-sidebar-nav4-sidebars-content-global-app-shortcuts.ui.teams.popup"
							messageType="transactional"
							onClose={useCallback(() => openTeamsPopup(false), [])}
							trigger={(triggerProps) => (
								<IconButton
									{...triggerProps}
									icon={(iconProps) => <AddIcon {...iconProps} size="small" />}
									appearance="subtle"
									spacing="compact"
									testId="navigation-apps-sidebar-nav4-sidebars-content-global-app-shortcuts.ui.teams.sidenav-teams-popup-trigger"
									isSelected={isOpenTeamsPopup}
									label={label}
									tooltip={{ content: label }}
									isTooltipDisabled={false}
									onClick={onPlusButtonClick}
								/>
							)}
							isOpen={isOpenTeamsPopup}
							content={renderContent}
						/>
					</>
				}
			>
				{formatMessage(messages.teams)}
			</LinkMenuItem>
		</>
	);
}

export function Teams(props: AppProps) {
	return (
		<InvitePeopleModalContextProvider>
			<TeamsInner {...props} />
		</InvitePeopleModalContextProvider>
	);
}

const teamsMenuStyle = xcss({
	minWidth: '260px',
	paddingBlock: 'space.100',
});
